import {
  ErrorOption,
  FieldValues,
  Path,
  PathValue,
  UseFormClearErrors,
  UseFormSetError,
  UseFormSetValue,
} from "react-hook-form";

export const formatCurrencyValue = (value: string | number | null): string => {
  if (value === null || value === "") return "";

  const raw = value.toString().replace(/[^0-9.]/g, "");
  const parts = raw.split(".");

  let formatted = parts[0]
    ? Number(parts[0].replace(/,/g, "")).toLocaleString("en-US")
    : "";

  if (parts.length > 1) {
    formatted += "." + parts[1].slice(0, 2);
  }

  return formatted;
};

// main reusable function for onChange
export function handleCurrencyChange<
  TFieldValues extends FieldValues,
  TFieldName extends Path<TFieldValues>
>(
  e: React.ChangeEvent<HTMLInputElement>,
  field: Path<TFieldValues>,
  setValue: UseFormSetValue<TFieldValues>,
  clearErrors: UseFormClearErrors<TFieldValues>,
  setError: UseFormSetError<TFieldValues>
) {
  const rawValue = e.target.value;

  if (rawValue.trim() === "") {
    setValue(field, "" as PathValue<TFieldValues, TFieldName>, {
      shouldValidate: true,
    });
    clearErrors(field);
    return;
  }

  if (/[^0-9.,]/.test(rawValue)) {
    setError(field, {
      type: "manual",
      message: "Only numbers are allowed.",
    } as ErrorOption);
    return;
  } else {
    clearErrors(field);
  }

  const formattedValue = formatCurrencyValue(rawValue);
  setValue(field, formattedValue as PathValue<TFieldValues, TFieldName>, {
    shouldValidate: true,
  });
}

export const formatDate = (isoDate: Date): string => {
  return isoDate
    .toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    })
    .replace(/\//g, ".");
};

export const formatTime = (isoDate: Date): string => {
  return isoDate.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

export const formatReportName = (name: string, createdAtDate: Date) => {
  const [prefix, timestamp] = name.split("_");

  if (prefix === "Report" && timestamp && !isNaN(Date.parse(timestamp))) {
    return `Report - ${formatDate(createdAtDate)} ${formatTime(createdAtDate)}`;
  }

  return name;
};

export const displayReportName = (
  reportName: string,
  templateName: string,
  createdAt: Date
) => {
  const createdAtDate = new Date(createdAt);

  return reportName
    ? formatReportName(reportName, createdAtDate)
    : templateName
    ? `${templateName} - ${formatDate(createdAtDate)} ${formatTime(
        createdAtDate
      )}`
    : `Report - ${formatDate(createdAtDate)} ${formatTime(createdAtDate)}`;
};
